'use client';

import { useEffect, useState } from 'react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/ui-lib/button';

interface BookmarkNameDialogProps {
    open: boolean;
    defaultValue: string;
    onConfirm: (name: string) => void;
    onCancel: () => void;
    title?: string;
    description?: string;
    submitLabel?: string;
}

export function BookmarkNameDialog({
    open,
    defaultValue,
    onConfirm,
    onCancel,
    title = 'Save Bookmark',
    description = 'Give this saved calculation a name to find it later.',
    submitLabel = 'Save',
}: BookmarkNameDialogProps) {
    const [value, setValue] = useState(defaultValue);

    // Re-seed the input every time the dialog opens, so a different entry's
    // suggested name shows up instead of the previous one.
    useEffect(() => {
        if (open) setValue(defaultValue);
    }, [open, defaultValue]);

    const trimmed = value.trim();
    const canSave = trimmed.length > 0;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSave) return;
        onConfirm(trimmed);
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
            <DialogContent theme="dark" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-neutral-100">{title}</DialogTitle>
                    <DialogDescription className="text-neutral-400">{description}</DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-3 py-2">
                    <Input
                        autoFocus
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Bookmark name"
                        aria-label="Bookmark name"
                        maxLength={200}
                    />

                    <DialogFooter>
                        <Button type="button" variant="secondary" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={!canSave}>
                            {submitLabel}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
